import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap, of, startWith } from 'rxjs';

import { PatternlibBreadcrumb } from '../../../stencil-generated/components';
import { Breakpoint } from '../../models/breakpoint';
import { Language } from '../../models/language';
import { NavigationEvent, NavigationEventOrigin } from '../../models/navigation';
import { UserAvatar } from '../../models/user';
import { BreadcrumbService } from '../../services/breadcrumb.service';
import { AdditionalLinksValue, LinksValue } from '../footer/footer.component';

export type ScreenSize = string;

@Component({
  selector: 'lh-portal-page',
  templateUrl: './portal-page.component.html',
  styleUrls: ['./portal-page.component.scss'],
})
export class PortalPageComponent implements AfterViewInit, OnInit {
  private readonly renderer2 = inject(Renderer2);
  private lastRelevantHeaderHeight = 0;
  private lastRelevantFooterHeight = 0;

  @Input('portal-name')
  portalName!: string;

  @Input()
  user: UserAvatar | null = null;

  @Input()
  languages: Language[] | null = null;

  @Input()
  activeLanguage: Language | null = null;

  @Input()
  enableAppLauncher = false;

  @Input()
  enableNotifications = false;

  @Input()
  helpLink: string | null = null;

  @Input()
  environment: string | null = null;

  @Input()
  accessToken: string | null = null;

  @Input()
  applauncherBaseUrl: string | null = null;

  @Input()
  communicationHubBaseUrl: string | null = null;

  @Output()
  onNavigation = new EventEmitter<NavigationEvent>();

  @Output()
  onLanguageChange = new EventEmitter<string>();

  @Output()
  clickBrandLogo = new EventEmitter();

  /**
   * 	Pass an array to the footer, which contains title and items
   */
  @Input()
  footerLinks: LinksValue[] = [];
  /**
   * Pass an array to the footer, which contains label and url
   */
  @Input()
  footerAdditionalLinks: AdditionalLinksValue[] = [];

  /**
   * If true shows an Icon in the footer which can be clicked to get back to the top of the current page.
   */
  @Input()
  footerShowGoToTopIcon = false;

  /**
   * If true the footer will be sticky at the bottom of the page.
   * If sticky logo and arrowToTop icon is not shown, height of the footer is reduced.
   */
  @Input()
  footerFixed = false;

  /**
   * Defines if sidebar is rendered.
   */
  @Input('sidebar')
  showSidebar = true;

  /**
   * Defines if search in main navigation is rendered.
   */
  @Input('search')
  showSearch = false;

  /**
   * Expanded state of the sidebar.
   */
  @Input()
  sidebarExpanded = false;

  /**
   * Indicates that the main navigation is sticky, therefore the sidebar should also be sticky on tablet/desktop.
   */
  navigationIsSticky = false;

  /**
   * Display state of the avatar flyout menu.
   */
  showAvatarFlyout = false;

  /**
   * The viewport breakpoint state.
   */
  currentBreakpoint: ScreenSize = 'isDesktop';

  @Output('breakpointChange')
  currentBreakpointChange = new EventEmitter<ScreenSize>();

  /**
   * Additional sidebar state, which hides the sidebar on mobile devices.
   */
  hideSidebar = false;

  /** Viewchild to the patternlib breadcrumb component. */
  @ViewChild('breadcrumbComponent')
  breadcrumbComponent?: PatternlibBreadcrumb;

  public backRoute: string | null = null;

  constructor(
    private breadcrumbService: BreadcrumbService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
    const observer = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'breakpoint') {
          const breakpoint = (mutation.target as Element).getAttribute('breakpoint');
          this.onBreakpointChange(breakpoint);
        }
      });
    });

    observer.observe(document.documentElement, {
      attributeFilter: ['breakpoint'],
      attributeOldValue: true,
      subtree: false,
      childList: false,
    });
  }

  ngOnInit(): void {
    this.onBreakpointChange(document.documentElement.getAttribute('breakpoint'));
    this.setBackRoute();
  }

  ngAfterViewInit(): void {
    this.breadcrumbService.component = (this.breadcrumbComponent as any).el;
  }

  isDesktopOrLarger() {
    return this.currentBreakpoint === 'isDesktop';
  }

  onAvatarFlyoutChange(show: any): void {
    this.showAvatarFlyout = show;
    if (show) {
      this.showLanguageFlyout = false;
    }
  }

  /**
   * Display state of the language flyout menu.
   */
  showLanguageFlyout = false;

  onLanguageFlyoutChange(show: any): void {
    this.showLanguageFlyout = show;
    if (show) {
      this.showAvatarFlyout = false;
    }
  }

  onMainNavigationClickCollect(event: CustomEvent<any>): void {
    this.onNavigation.emit(
      new NavigationEvent(
        event.detail.itemId,
        event.detail.subitemId,
        event.detail.subSubitemId,
        NavigationEventOrigin.MainNav,
      ),
    );
  }

  onSidebarChangeCollect(event: CustomEvent<any>): void {
    this.onNavigation.emit(
      new NavigationEvent(
        event.detail.itemId,
        event.detail.subitemId,
        event.detail.subSubitemId,
        NavigationEventOrigin.Sidebar,
      ),
    );
  }

  onSidebarCollapseCollect(event: CustomEvent<any>): void {
    this.sidebarExpanded = !event?.detail?.isCollapsed || false;
    this.updateComponentSizes(this.lastRelevantHeaderHeight, this.lastRelevantFooterHeight);
  }

  onBreadcrumbClickCollect(event: CustomEvent<any>): void {
    this.onNavigation.emit(
      new NavigationEvent(
        event.detail.itemId || event.detail.breadcrumbItemId,
        null,
        null,
        NavigationEventOrigin.Breadcrumb,
      ),
    );
  }

  onMainNaviationSticky(event: CustomEvent<any>): void {
    this.navigationIsSticky = event.detail.isSticky;
  }

  selectLanguage(code: string): void {
    this.onLanguageChange.emit(code);
    this.showLanguageFlyout = false;
  }

  get MobileBreakpoint() {
    return Breakpoint.Mobile;
  }

  sidebarIsVisible(): boolean {
    return this.showSidebar && !this.hideSidebar;
  }

  sidebarIsVisibleAndExpanded(): boolean {
    return this.sidebarIsVisible() && this.sidebarExpanded;
  }

  onBreakpointChange(screenSize: any) {
    this.hideSidebar = false;
    this.currentBreakpoint = screenSize;

    if (screenSize === 'isMobile' || screenSize == 'isTablet') {
      this.hideSidebar = true;
      this.sidebarExpanded = false;
    }

    this.currentBreakpointChange.emit(this.currentBreakpoint);
  }

  onClickBrandLogo() {
    this.clickBrandLogo.emit();
  }

  getMainNavigationPadding() {
    return this.currentBreakpoint === 'isDesktop' ? '0 120px' : '0 20px';
  }

  onHelpClick() {
    window.open(this.helpLink!, '_blank');
  }

  private setBackRoute() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(this.router),
        map(() => this.activatedRoute.firstChild),
        map((route: any) => {
          while (route?.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap((route: ActivatedRoute | null) => (route ? route.data : of({}))),
        map((data: any) => data['backRoute']),
      )
      .subscribe((backRoute: string) => {
        this.backRoute = backRoute ? backRoute : null;
      });
  }

  public onHeaderResizeEvent(event: any): void {
    this.updateComponentSizes(event.h, this.lastRelevantFooterHeight);
  }

  public onFooterResizeEvent(event: any): void {
    this.updateComponentSizes(this.lastRelevantHeaderHeight, event.h);
  }

  private updateComponentSizes(headerHeight: number, footerHeight: number): void {
    const contentRef = document.querySelector('#full-content-container') as HTMLDivElement;
    if (contentRef == null) return;

    // when headerHeight === 0
    // then:  - header is sticky (position: fixed)
    //        - margin-top fills the difference
    let contentMarginTop = '0px';
    if (headerHeight > 0) {
      this.lastRelevantHeaderHeight = headerHeight;
    } else {
      contentMarginTop = `${this.lastRelevantHeaderHeight}px`;
    }
    this.lastRelevantFooterHeight = footerHeight;

    const sidebarColumnRef = document.querySelector('#sidebar-column') as HTMLDivElement;
    const sidebarStickyRef = document.querySelector('#sidebar-sticky') as HTMLDivElement;
    const footerRef = document.querySelector('#lh-footer') as HTMLDivElement;
    // is mobile
    const mediaQueryTabDesktop = window.matchMedia('(min-width: 768px)');
    const isMobile = !mediaQueryTabDesktop.matches;

    const contentHeight = `calc(100vh - ${this.lastRelevantHeaderHeight}px - ${footerHeight}px)`;
    const sidebarTop = `${this.lastRelevantHeaderHeight}px`;
    const sidebarHeight = `calc(100vh - ${this.lastRelevantHeaderHeight}px`;

    // content
    this.renderer2.setStyle(contentRef, 'margin-top', contentMarginTop);
    this.renderer2.setStyle(contentRef, 'min-height', contentHeight);

    // sidebar
    let sidebarWidth = '0px';
    if (sidebarColumnRef != null) {
      this.renderer2.setStyle(sidebarColumnRef, 'height', sidebarHeight);
    }
    if (sidebarStickyRef != null) {
      this.renderer2.setStyle(sidebarStickyRef, 'top', sidebarTop);
      this.renderer2.setStyle(sidebarStickyRef, 'height', sidebarHeight);
      sidebarWidth = this.sidebarExpanded ? '280px' : '80px';
    }

    // footer width
    let footerWidth = '100%';
    if (sidebarStickyRef != null && this.footerFixed && !isMobile) {
      footerWidth = `calc(100% - ${sidebarWidth})`;
    }
    this.renderer2.setStyle(footerRef, 'width', footerWidth);

    // footer - fixed/sticky
    if (this.footerFixed && !isMobile) {
      this.renderer2.setStyle(footerRef, 'position', 'fixed');
      this.renderer2.setStyle(footerRef, 'bottom', '0px');
      this.renderer2.setStyle(footerRef, 'left', sidebarWidth);
      this.renderer2.setStyle(footerRef, 'z-index', 50); // identical to header
      this.renderer2.setStyle(contentRef, 'margin-bottom', `${footerHeight}px`);
    } else {
      this.renderer2.setStyle(footerRef, 'position', 'static');
      this.renderer2.removeStyle(footerRef, 'bottom');
      this.renderer2.removeStyle(footerRef, 'left');
      this.renderer2.removeStyle(footerRef, 'z-index');
      this.renderer2.removeStyle(contentRef, 'margin-bottom');
    }
  }
}
